import { Form } from "react-bootstrap";
import { useActuatorConfigurationContext } from "context/ActuatorConfigurationContext";
import levelHighIcon from "assets/svg/level-high.svg";
import levelLowIcon from "assets/svg/level-low.svg";
import pulsePositiveIcon from "assets/svg/pulse-positive.svg";
import pulseNegativeIcon from "assets/svg/pulse-negative.svg";
import loopPositiveIcon from "assets/svg/loop-positive.svg";
import loopNegativeIcon from "assets/svg/loop-negative.svg";
import pwmPositiveIcon from "assets/svg/pwm-positive.svg";
import pwmNegativeIcon from "assets/svg/pwm-negative.svg";
import styled from "@emotion/styled";
import { ActuatorCommandMode } from "types/Actuator";

const PolarityBox = styled.div`
    width: 100%;
    max-width: 700px;
    min-width: 330px;
    margin-bottom: 10px;
    flex: 1;

    p {
        color: #fff;
    }
`;

export const PolarityOptions = ({
    polarity,
    mode,
    onChange,
    variant = "default",
}: {
    polarity?: boolean;
    mode?: string;
    onChange: (value: boolean) => void;
    variant?: "default" | "light";
}) => {
    const renderLabel = ({
        mode,
        isPositive,
    }: {
        mode?: string;
        isPositive: boolean;
    }) => {
        if (mode === ActuatorCommandMode.LEVEL) return isPositive ? "High" : "Low";
        return isPositive ? "Positive" : "Negative";
    };

    const renderImage = ({
        mode,
        isPositive,
    }: {
        mode?: string;
        isPositive: boolean;
    }) => {
        switch (mode) {
            case ActuatorCommandMode.LEVEL:
                return isPositive ? levelHighIcon : levelLowIcon;
            case ActuatorCommandMode.PULSE:
                return isPositive ? pulsePositiveIcon : pulseNegativeIcon;
            case ActuatorCommandMode.LOOP:
                return isPositive ? loopPositiveIcon : loopNegativeIcon;
            case ActuatorCommandMode.PWM:
                return isPositive ? pwmPositiveIcon : pwmNegativeIcon;
            default:
                return undefined;
        }
    };

    return (
        <Form.Row className="mt-3 flex-row gap-3 justify-space-center">
            <PolarityBox className="col">
                <Form.Check
                    className="polarity-selection"
                    name="polarity"
                    id={"polarity-positive"}
                    onChange={() => {
                        onChange(true);
                    }}
                    checked={polarity}
                    custom
                    type="radio"
                    label={
                        <>
                            <img
                                src={renderImage({
                                    mode: mode,
                                    isPositive: true,
                                })}
                                alt=""
                            />
                            <p className="mt-4">
                                {renderLabel({
                                    mode: mode,
                                    isPositive: true,
                                })}
                            </p>
                        </>
                    }
                />
            </PolarityBox>
            <PolarityBox>
                <Form.Check
                    className="polarity-selection"
                    name="polarity"
                    id={"polarity-negative"}
                    onChange={() => {
                        onChange(false);
                    }}
                    checked={!polarity}
                    custom
                    type="radio"
                    label={
                        <>
                            <img
                                src={renderImage({
                                    mode: mode,
                                    isPositive: false,
                                })}
                                alt=""
                            />
                            <p className="mt-4">
                                {renderLabel({
                                    mode: mode,
                                    isPositive: false,
                                })}
                            </p>
                        </>
                    }
                />
            </PolarityBox>
        </Form.Row>
    );
};

const _PolarityOptions = () => {
    const {
        editedActuator,
        editedActuator: { polarity, mode },
        setEditedActuator,
    } = useActuatorConfigurationContext();

    const togglePolarity = (polarity: boolean) => {
        setEditedActuator({
            ...editedActuator,
            polarity,
        });
    };

    return (
        <>
            <Form.Label className="mt-3">Polarity</Form.Label>
            <PolarityOptions
                polarity={polarity}
                mode={mode}
                onChange={(value) => togglePolarity(value)}
            />
        </>
    );
};

export default _PolarityOptions;
